import { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import PublicHeader from '@/components/layout/PublicHeader';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Card, CardContent } from '@/components/ui/card';
import { Target, ArrowRight, TrendingUp, BarChart3, Users, Sparkles, Share2, Download, Lock, Loader2, Copy } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PILLARS = [
  { key: 'overall',    label: 'Overall' },
  { key: 'process',    label: 'Process' },
  { key: 'people',     label: 'People' },
  { key: 'technology', label: 'Technology' },
];

// Same colour scale as the admin heatmap — the marketing copy references "red vs green"
// so keep them visually identical for cross-channel consistency.
const colorFor = (score) => {
  if (score == null)    return { bg: '#F3F4F6', fg: '#9CA3AF' };
  if (score < 45)       return { bg: '#DC2626', fg: '#FFFFFF' };
  if (score < 55)       return { bg: '#F97316', fg: '#FFFFFF' };
  if (score < 65)       return { bg: '#F59E0B', fg: '#0A0A0A' };
  if (score < 75)       return { bg: '#10B981', fg: '#FFFFFF' };
  if (score < 85)       return { bg: '#059669', fg: '#FFFFFF' };
  return { bg: '#047857', fg: '#FFFFFF' };
};

/**
 * Public marketing landing page — no auth, no sidebar.
 *
 * Hook: "See how your KM maturity stacks up against your industry" →
 * show the heatmap (same as the admin tool) minus every editorial affordance,
 * then CTA to "Benchmark your own" which routes to /register.
 *
 * SEO angle: these are published KM-research norms competitors can cite anyway;
 * putting them behind a login wall only hurts discovery.
 */
const AtlasPage = () => {
  const navigate = useNavigate();
  const { miniId: urlMiniId } = useParams();  // /atlas/mini/:miniId share route
  const [searchParams] = useSearchParams();
  // A/B attribution — set by the marketing CTAs via ?utm_variant=&utm_source=.
  const utmVariant = (searchParams.get('utm_variant') || '').toLowerCase();
  const utmSource = searchParams.get('utm_source') || '';
  const abSessionId = (typeof window !== 'undefined' && localStorage.getItem('kstreak_cta_session')) || null;
  const [data, setData] = useState(null);
  const [pillar, setPillar] = useState('overall');
  const [loading, setLoading] = useState(true);

  // Mini-benchmark state
  const [questions, setQuestions] = useState([]);
  const [miniIndustry, setMiniIndustry] = useState('tech');
  const [miniSize, setMiniSize] = useState('51-250');
  const [miniAnswers, setMiniAnswers] = useState({});
  const [miniEmail, setMiniEmail] = useState('');
  const [miniSubmitting, setMiniSubmitting] = useState(false);
  const [miniResult, setMiniResult] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [atlasR, qR] = await Promise.all([
          axios.get(`${API}/km-benchmarking/atlas`),
          axios.get(`${API}/km-benchmarking/atlas/mini/questions`),
        ]);
        setData(atlasR.data);
        setQuestions(qR.data.questions || []);
      } catch {
        /* silent — degrade to empty state */
      } finally { setLoading(false); }
    })();
  }, []);

  // If someone lands on /atlas/mini/:id, auto-load that shared result.
  useEffect(() => {
    if (!urlMiniId) return;
    (async () => {
      try {
        const r = await axios.get(`${API}/km-benchmarking/atlas/mini/${urlMiniId}`);
        setMiniResult(r.data);
        setMiniIndustry(r.data.industry);
        setMiniSize(r.data.company_size);
        setTimeout(() => document.getElementById('mini')?.scrollIntoView({ behavior: 'smooth' }), 300);
      } catch {
        toast.error('That mini-benchmark link is invalid or expired');
      }
    })();
  }, [urlMiniId]);

  // Share the heatmap as a URL with the selected pillar pre-applied.
  const shareHeatmap = async () => {
    const url = `${window.location.origin}/atlas?pillar=${pillar}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Heatmap link copied to clipboard');
    } catch {
      toast.info(`Copy this link: ${url}`);
    }
  };

  const downloadHeatmap = async () => {
    try {
      const r = await axios.get(`${API}/km-benchmarking/atlas/heatmap.pdf`, {
        params: { pillar }, responseType: 'blob',
      });
      const href = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = href;
      a.download = `km-atlas-heatmap-${pillar}.pdf`;
      a.click();
      URL.revokeObjectURL(href);
    } catch {
      toast.error('Heatmap download failed');
    }
  };

  const submitMini = async () => {
    // Must have answered all 5.
    if (questions.some(q => !miniAnswers[q.id])) {
      toast.error('Please answer all 5 questions');
      return;
    }
    setMiniSubmitting(true);
    try {
      const r = await axios.post(`${API}/km-benchmarking/atlas/mini`, {
        industry: miniIndustry,
        company_size: miniSize,
        answers: miniAnswers,
        email: miniEmail || undefined,
        utm_variant: utmVariant || undefined,
        utm_source: utmSource || undefined,
        session_id: abSessionId || undefined,
      });
      setMiniResult(r.data);
      setTimeout(() => document.getElementById('mini-result')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 300);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Submission failed');
    } finally { setMiniSubmitting(false); }
  };

  const shareMini = async () => {
    if (!miniResult) return;
    const url = `${window.location.origin}/atlas/mini/${miniResult.mini_id}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Result link copied to clipboard');
    } catch {
      toast.info(`Copy this link: ${url}`);
    }
  };

  const downloadMini = async () => {
    if (!miniResult) return;
    try {
      const r = await axios.get(`${API}/km-benchmarking/atlas/mini/${miniResult.mini_id}/download.pdf`, {
        responseType: 'blob',
      });
      const href = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = href;
      a.download = `km-mini-benchmark-${miniResult.mini_id}.pdf`;
      a.click();
      URL.revokeObjectURL(href);
    } catch {
      toast.error('PDF download failed');
    }
  };

  const retakeMini = () => {
    setMiniResult(null);
    setMiniAnswers({});
    setMiniEmail('');
  };

  // O(1) lookup for the grid
  const lookup = (data?.benchmarks || []).reduce((acc, b) => {
    acc[`${b.industry}|${b.company_size}`] = b;
    return acc;
  }, {});

  const activePillarLabel = PILLARS.find(p => p.key === pillar)?.label || 'Overall';

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#FAFAFA] to-white" data-testid="atlas-page">
      <PublicHeader
        extraButtons={
          <Button size="sm" className="bg-[#002FA7] hover:bg-[#00227A]" onClick={() => navigate('/register')} data-testid="atlas-cta-btn">
            Benchmark my org<ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        }
      />

      {/* Hero */}
      <section className="px-4 sm:px-8 pt-12 pb-8 max-w-6xl mx-auto text-center">
        <Badge variant="outline" className="mb-4 text-[10px] uppercase tracking-wider">
          <Sparkles className="w-3 h-3 mr-1 text-[#002FA7]" />
          Free · No sign-up required
        </Badge>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-[#0A0A0A] leading-[1.05]">
          The KM Benchmark Atlas
        </h1>
        <p className="mt-4 text-base sm:text-lg text-[#4B5563] max-w-2xl mx-auto leading-relaxed">
          How does your organisation's Knowledge Management maturity compare to peers in your industry?
          Explore curated norms for <strong>8 industries × 4 company sizes</strong>, calibrated from APQC,
          KM Institute, and Gartner public research.
        </p>
        <div className="mt-8 flex items-center justify-center gap-3 flex-wrap">
          <Button
            size="lg"
            className="bg-[#002FA7] hover:bg-[#00227A] h-11"
            onClick={() => document.getElementById('mini')?.scrollIntoView({ behavior: 'smooth' })}
            data-testid="atlas-hero-mini-btn"
          >
            <BarChart3 className="w-4 h-4 mr-2" />
            Take the 2-minute benchmark
          </Button>
          <Button
            variant="outline"
            size="lg"
            className="h-11"
            onClick={() => navigate('/register')}
            data-testid="atlas-hero-cta-btn"
          >
            Create a free account<ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </section>

      {/* Heatmap */}
      <section id="heatmap" className="px-4 sm:px-8 py-10 max-w-6xl mx-auto">
        <div className="flex items-end justify-between mb-4 flex-wrap gap-3">
          <div>
            <h2 className="text-2xl font-semibold text-[#0A0A0A]">Industry heatmap</h2>
            <p className="text-sm text-[#4B5563] mt-0.5">
              Cells show the <strong>P50 (industry median)</strong> on the <strong>{activePillarLabel}</strong> pillar.
              Switch the pillar to explore Process, People, or Technology.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Select value={pillar} onValueChange={setPillar}>
              <SelectTrigger className="w-[180px]" data-testid="atlas-pillar-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PILLARS.map(p => <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button
              variant="outline" size="sm"
              onClick={shareHeatmap}
              className="h-10"
              data-testid="atlas-heatmap-share-btn"
            >
              <Share2 className="w-3.5 h-3.5 mr-1.5" />Share
            </Button>
            <Button
              variant="outline" size="sm"
              onClick={downloadHeatmap}
              className="h-10"
              data-testid="atlas-heatmap-download-btn"
            >
              <Download className="w-3.5 h-3.5 mr-1.5" />PDF
            </Button>
          </div>
        </div>

        {/* Legend */}
        <div className="flex items-center gap-2 text-[10px] text-[#4B5563] mb-3 flex-wrap" data-testid="atlas-legend">
          <span className="uppercase tracking-wider text-[#9CA3AF] font-semibold">P50 scale</span>
          {[
            { l: '<45',  c: '#DC2626' },
            { l: '45-54', c: '#F97316' },
            { l: '55-64', c: '#F59E0B' },
            { l: '65-74', c: '#10B981' },
            { l: '75-84', c: '#059669' },
            { l: '85+',   c: '#047857' },
          ].map(s => (
            <span key={s.l} className="flex items-center gap-1">
              <span className="inline-block w-3 h-3 rounded-sm" style={{ background: s.c }} />
              <span>{s.l}</span>
            </span>
          ))}
        </div>

        {/* Grid */}
        {loading ? (
          <div className="p-16 text-center text-sm text-[#9CA3AF]">Loading benchmarks…</div>
        ) : !data ? (
          <div className="p-16 text-center text-sm text-[#9CA3AF]">Benchmarks temporarily unavailable.</div>
        ) : (
          <div className="overflow-x-auto bg-white border border-[#E5E7EB] rounded-lg p-4" data-testid="atlas-heatmap">
            <table className="w-full border-separate" style={{ borderSpacing: '4px' }}>
              <thead>
                <tr>
                  <th className="text-[10px] uppercase tracking-wide text-[#9CA3AF] text-left p-2"></th>
                  {data.sizes.map(s => (
                    <th key={s.value} className="text-[10px] uppercase tracking-wide text-[#9CA3AF] text-center p-2">
                      {s.value}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.industries.map(ind => (
                  <tr key={ind.value}>
                    <th className="text-xs text-[#0A0A0A] text-right p-2 font-medium whitespace-nowrap">
                      {ind.label}
                    </th>
                    {data.sizes.map(sz => {
                      const bm = lookup[`${ind.value}|${sz.value}`];
                      const score = bm?.percentiles?.[pillar]?.p50;
                      const col = colorFor(score);
                      return (
                        <td
                          key={sz.value}
                          className="p-0"
                          data-testid={`atlas-cell-${ind.value}-${sz.value}`}
                          title={bm ? `${ind.label} / ${sz.value}\nP50 ${activePillarLabel} = ${score}` : ''}
                        >
                          <div
                            className="w-full min-w-[90px] h-16 rounded-md flex items-center justify-center font-bold text-lg"
                            style={{ background: col.bg, color: col.fg }}
                          >
                            {score ?? '—'}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <p className="text-[11px] text-[#9CA3AF] mt-3 text-center">
          Source: APQC / KM Institute / Gartner composite, calibrated Q1 2026.
        </p>

        {/* Inline "Benchmark" CTA between the heatmap and the mini form — the strongest
            conversion moment, because the visitor has just looked at the grid and is
            now primed to wonder "where would MY org land?". Smooth-scrolls to the
            5-question form instead of forcing a register-wall. */}
        <div className="mt-8 flex justify-center">
          <Button
            size="lg"
            onClick={() => document.getElementById('mini')?.scrollIntoView({ behavior: 'smooth' })}
            className="bg-[#002FA7] hover:bg-[#00227A] h-12 px-6 text-base shadow-md"
            data-testid="atlas-inline-benchmark-btn"
          >
            <BarChart3 className="w-5 h-5 mr-2" />
            Benchmark my org now
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </section>

      {/* Mini Benchmark section — 5-Q capture form OR the result card if submitted */}
      <section id="mini" className="px-4 sm:px-8 py-12 max-w-4xl mx-auto" data-testid="atlas-mini-section">
        {!miniResult ? (
          <Card className="border-[#E5E7EB]">
            <CardContent className="p-6">
              <div className="text-center mb-5">
                <Badge variant="outline" className="mb-3 text-[10px] uppercase tracking-wider">
                  <Sparkles className="w-3 h-3 mr-1 text-[#002FA7]" />
                  2-minute quick check
                </Badge>
                <h2 className="text-2xl sm:text-3xl font-semibold text-[#0A0A0A]">
                  How does your org stack up?
                </h2>
                <p className="text-sm text-[#4B5563] mt-2 max-w-xl mx-auto">
                  Answer 5 quick questions and see your instant mini-benchmark against industry peers.
                  No sign-up required.
                </p>
              </div>

              {/* Industry + Size */}
              <div className="grid sm:grid-cols-2 gap-3 mb-5">
                <div>
                  <Label className="text-xs text-[#4B5563]">Industry</Label>
                  <Select value={miniIndustry} onValueChange={setMiniIndustry}>
                    <SelectTrigger data-testid="atlas-mini-industry"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {(data?.industries || []).map(o => (
                        <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-[#4B5563]">Company size</Label>
                  <Select value={miniSize} onValueChange={setMiniSize}>
                    <SelectTrigger data-testid="atlas-mini-size"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {(data?.sizes || []).map(o => (
                        <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Questions */}
              <div className="space-y-4" data-testid="atlas-mini-questions">
                {questions.map((q, idx) => (
                  <div key={q.id} className="p-3 bg-[#FAFAFA] rounded-md">
                    <p className="text-sm text-[#0A0A0A] mb-2">
                      <span className="text-[#9CA3AF] mr-1.5">{idx + 1}.</span>{q.text}
                    </p>
                    <div className="flex gap-1.5 flex-wrap">
                      {[
                        { v: 1, l: 'Strongly\u00a0disagree' },
                        { v: 2, l: 'Disagree' },
                        { v: 3, l: 'Neutral' },
                        { v: 4, l: 'Agree' },
                        { v: 5, l: 'Strongly\u00a0agree' },
                      ].map(o => (
                        <button
                          key={o.v}
                          type="button"
                          onClick={() => setMiniAnswers(a => ({ ...a, [q.id]: o.v }))}
                          className={`flex-1 min-w-[85px] px-2 py-1.5 rounded border text-[11px] transition-colors ${
                            miniAnswers[q.id] === o.v
                              ? 'bg-[#002FA7] text-white border-[#002FA7]'
                              : 'bg-white text-[#4B5563] border-[#E5E7EB] hover:border-[#002FA7]'
                          }`}
                          data-testid={`atlas-mini-answer-${q.id}-${o.v}`}
                        >
                          {o.l}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-5 grid sm:grid-cols-[1fr_auto] gap-3">
                <Input
                  type="email"
                  value={miniEmail}
                  onChange={(e) => setMiniEmail(e.target.value)}
                  placeholder="Optional — email me the full report when I'm ready"
                  className="text-sm"
                  data-testid="atlas-mini-email"
                />
                <Button
                  onClick={submitMini}
                  disabled={miniSubmitting}
                  className="bg-[#002FA7] hover:bg-[#00227A] h-10"
                  data-testid="atlas-mini-submit"
                >
                  {miniSubmitting
                    ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" />
                    : <BarChart3 className="w-4 h-4 mr-1.5" />}
                  See my mini-benchmark
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card className="border-[#002FA7]/30 shadow-md" id="mini-result" data-testid="atlas-mini-result">
            <CardContent className="p-6">
              <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
                <div>
                  <Badge className="text-[10px] uppercase tracking-wider bg-[#002FA7]/10 text-[#002FA7] border border-[#002FA7]/30 mb-2">
                    <Sparkles className="w-3 h-3 mr-1" />
                    Your mini-benchmark
                  </Badge>
                  <h2 className="text-2xl font-semibold text-[#0A0A0A]">
                    {(data?.industries || []).find(i => i.value === miniResult.industry)?.label || miniResult.industry}
                    <span className="text-[#9CA3AF] text-base"> · {miniResult.company_size}</span>
                  </h2>
                </div>
                <div className="flex gap-1.5">
                  <Button variant="outline" size="sm" onClick={shareMini} className="h-9" data-testid="atlas-mini-share-btn">
                    <Copy className="w-3.5 h-3.5 mr-1.5" />Share link
                  </Button>
                  <Button variant="outline" size="sm" onClick={downloadMini} className="h-9" data-testid="atlas-mini-download-btn">
                    <Download className="w-3.5 h-3.5 mr-1.5" />PDF
                  </Button>
                  <Button variant="ghost" size="sm" onClick={retakeMini} className="h-9 text-[11px]" data-testid="atlas-mini-retake-btn">
                    Retake
                  </Button>
                </div>
              </div>

              {/* Pillar score rows */}
              <div className="space-y-2" data-testid="atlas-mini-scores">
                {(miniResult.gap_analysis || []).filter(g => g.pillar !== 'overall').map(g => {
                  const tone = g.gap_pts > 10 ? 'text-[#FF3B30]' : g.gap_pts > 3 ? 'text-[#FF9500]' : 'text-[#34C759]';
                  return (
                    <div key={g.pillar} className="flex items-center gap-3 p-3 bg-[#FAFAFA] rounded-md">
                      <span className="text-xs font-semibold text-[#0A0A0A] capitalize w-24">{g.pillar}</span>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 text-[11px] text-[#4B5563]">
                          <span>Your: <strong className="text-[#0A0A0A]">{g.org}</strong></span>
                          <span>·</span>
                          <span>Industry P50: <strong className="text-[#0A0A0A]">{g.benchmark_p50}</strong></span>
                        </div>
                        {/* Bar */}
                        <div className="relative h-1.5 bg-[#E5E7EB] rounded-full mt-1.5 overflow-hidden">
                          <div className="absolute top-0 left-0 h-full bg-[#002FA7]" style={{ width: `${Math.min(100, g.org)}%` }} />
                          <div className="absolute top-0 h-full w-[2px] bg-[#AF52DE]" style={{ left: `${g.benchmark_p50}%` }} title={`P50: ${g.benchmark_p50}`} />
                        </div>
                      </div>
                      <span className={`text-xs font-bold ${tone}`}>
                        {g.gap_pts > 0 ? '−' : '+'}{Math.abs(g.gap_pts).toFixed(1)}
                      </span>
                    </div>
                  );
                })}
              </div>

              {/* Biggest gap callout */}
              {miniResult.biggest_gap && (
                <div className="mt-4 p-3 bg-[#FF9500]/5 border-l-3 border-[#FF9500] rounded-md" data-testid="atlas-mini-biggest-gap">
                  <p className="text-[10px] uppercase tracking-wide text-[#FF9500] font-semibold">Biggest opportunity</p>
                  <p className="text-sm text-[#0A0A0A] mt-0.5">
                    Your <strong className="capitalize">{miniResult.biggest_gap.pillar}</strong> pillar is{' '}
                    <strong>{Math.abs(miniResult.biggest_gap.gap_pts).toFixed(1)} points</strong>{' '}
                    {miniResult.biggest_gap.gap_pts > 0 ? 'behind' : 'ahead of'} the industry median.
                  </p>
                </div>
              )}

              {/* Locked insights + CTA */}
              <div className="mt-5 p-4 rounded-lg bg-gradient-to-br from-[#002FA7]/5 to-[#002FA7]/10 border border-[#002FA7]/20 text-center" data-testid="atlas-mini-locked">
                <Lock className="w-8 h-8 text-[#002FA7] mx-auto mb-2 opacity-60" />
                <p className="text-sm font-semibold text-[#0A0A0A]">Unlock the full benchmark</p>
                <p className="text-xs text-[#4B5563] mt-1.5 max-w-md mx-auto leading-relaxed">
                  See AI-generated root-cause analysis, 6-KPI projected uplift, radar chart against P50/P75,
                  and week-over-week trajectory. <strong>Free account · 10 minutes</strong>.
                </p>
                <Button
                  size="lg"
                  className="mt-3 bg-[#002FA7] hover:bg-[#00227A] h-11"
                  onClick={() => navigate('/register')}
                  data-testid="atlas-mini-register-btn"
                >
                  Get my full benchmark report<ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </section>

      {/* Value props */}
      <section className="px-4 sm:px-8 py-12 max-w-5xl mx-auto">
        <div className="grid gap-6 sm:grid-cols-3">
          <div className="p-5 rounded-lg border border-[#E5E7EB] bg-white" data-testid="atlas-value-1">
            <div className="w-9 h-9 rounded-md bg-[#002FA7]/10 flex items-center justify-center mb-3">
              <BarChart3 className="w-4 h-4 text-[#002FA7]" />
            </div>
            <h3 className="text-base font-semibold text-[#0A0A0A]">See your gap</h3>
            <p className="text-xs text-[#4B5563] mt-1 leading-relaxed">
              Radar chart comparing your org to peer P50/P75 with per-pillar severity badges
              and root-cause narratives.
            </p>
          </div>
          <div className="p-5 rounded-lg border border-[#E5E7EB] bg-white" data-testid="atlas-value-2">
            <div className="w-9 h-9 rounded-md bg-[#002FA7]/10 flex items-center justify-center mb-3">
              <TrendingUp className="w-4 h-4 text-[#002FA7]" />
            </div>
            <h3 className="text-base font-semibold text-[#0A0A0A]">Quantify the upside</h3>
            <p className="text-xs text-[#4B5563] mt-1 leading-relaxed">
              Projected uplift across 6 KPIs if you close the P50 gap: time-to-competency,
              productivity, retention, onboarding, rework rate, innovation rate.
            </p>
          </div>
          <div className="p-5 rounded-lg border border-[#E5E7EB] bg-white" data-testid="atlas-value-3">
            <div className="w-9 h-9 rounded-md bg-[#002FA7]/10 flex items-center justify-center mb-3">
              <Sparkles className="w-4 h-4 text-[#002FA7]" />
            </div>
            <h3 className="text-base font-semibold text-[#0A0A0A]">AI recommendations</h3>
            <p className="text-xs text-[#4B5563] mt-1 leading-relaxed">
              GPT-5.2 reads your scorecard and returns prioritised recommendations
              tagged by pillar and effort — ready to hand to your KM team.
            </p>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="px-4 sm:px-8 py-16 max-w-3xl mx-auto text-center">
        <h2 className="text-3xl sm:text-4xl font-semibold text-[#0A0A0A]">
          Stop guessing where your KM stands.
        </h2>
        <p className="mt-3 text-base text-[#4B5563]">
          Run your first benchmark in under 10 minutes. Free tier includes full gap analysis,
          AI insights, and PDF export.
        </p>
        <Button
          size="lg"
          className="mt-6 bg-[#002FA7] hover:bg-[#00227A] h-12 text-base"
          onClick={() => navigate('/register')}
          data-testid="atlas-final-cta-btn"
        >
          <Users className="w-4 h-4 mr-2" />
          Start benchmarking — free
          <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </section>

      {/* Footer */}
      <footer className="px-4 sm:px-8 py-6 border-t border-[#E5E7EB] flex flex-col items-center gap-2" data-testid="atlas-footer">
        <div className="flex items-center gap-2">
          <img src="/logo.png" alt="KStreak" className="w-6 h-6 rounded object-contain" />
          <span className="text-sm font-medium text-[#4B5563]">KStreak</span>
        </div>
        <p className="text-[11px] text-[#9CA3AF] text-center">
          © 2026 KStreak · Benchmarks derived from public APQC, KM Institute, and Gartner research.
        </p>
      </footer>
    </div>
  );
};

export default AtlasPage;
